import SvgIcon from '@/pages/home/components/SvgIcon';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bubble, Radio, Button, Dropdown, Icon } from 'tea-component';
import FeedsEditBox from '@op/pages/feeds/components/FeedsEditbox/index';
import useFeedsUpdate, { PageUpdateType } from '../../hooks/useFeedsUpdate';
import { getSentimentIcon } from '../../utils';
import { useIntl } from 'umi';
import BubbleCommon, { NLPAnalyseTipsText } from '../BubbleCommon';
import { sentimentTypeMap } from '../ReviewsModal/constants';
import { useSentimentRating } from '../ReviewsModal/useSentimentRating';
import './index.scss';
import { SentimentType } from '@op/types/common';
import { FilterContext } from '@op/pages/Summary/context';
import _ from 'lodash-es';

function SentimentEditBox({
  defaultValue,
  oldValue,
  filter,
  close,
  Sentiment_Update,
  changeNewSentiment,
  type = 'common',
  showValue,
  subTopic,
  changeNewSubTopic,
}: {
  defaultValue: SentimentType;
  oldValue: SentimentType;
  filter: any;
  close: () => void;
  Sentiment_Update: boolean;
  changeNewSentiment: (value: SentimentType) => void;
  type?: PageUpdateType;
  showValue?: string;
  subTopic?: Array<any>;
  changeNewSubTopic: (value: Array<any>) => void;
}) {
  const {
    logLoading,
    loading,
    submit,
    setNewValue,
    newValue,
    modifiedValue,
    logList,
    subTopicValue,
    updateSubTopicValue,
    submitSubTopic,
  } = useFeedsUpdate<SentimentType>({
    defaultValue,
    oldValue,
    filter,
    showSystemLog: !Sentiment_Update,
    type,
    subTopic,
  });
  const { formatMessage } = useIntl();
  const { transTopic } = useContext(FilterContext);

  const disabled = useMemo(() => {
    if (logList.length === 0 && modifiedValue === newValue) return false;
    const isSame = _.isEqual(subTopic, subTopicValue);

    return (
      (modifiedValue === newValue || !newValue || newValue === '0') && isSame
    );
  }, [logList, modifiedValue, newValue, subTopic, subTopicValue]);

  const tips = useMemo(() => {
    if (!disabled) return null;
    if (modifiedValue === newValue) {
      return formatMessage({ id: 'Please modify before submitting' });
    }
    if (!newValue) {
      return formatMessage({ id: 'Please select an option' });
    }
    return null;
  }, [formatMessage, disabled]);

  return (
    <FeedsEditBox
      hd={
        <>
          <span className="feeds-editbox-title">
            {formatMessage({ id: 'Sentiment Editing' })}
          </span>
        </>
      }
      bd={
        <>
          {/* <span className="feeds-editbox-title">
            {formatMessage({ id: 'Whole Sentence Sentiment' })}
            <BubbleCommon
              placement="top"
              iconStyle={{ marginLeft: 6 }}
              tipsMap={commonSentimentTips}
            ></BubbleCommon>
          </span> */}
          <div className="feeds-editbox-radiobox">
            <Radio.Group
              value={newValue}
              onChange={(value) => setNewValue(value as SentimentType)}
            >
              {showValue === '0' && (
                <Radio name="0">
                  <SvgIcon
                    className="editbox-item-icon editbox-item-icon--24"
                    type="face-question"
                  />
                </Radio>
              )}
              <Radio name="5">
                <SvgIcon
                  className="editbox-item-icon editbox-item-icon--24"
                  type="laugh-green-color"
                />
              </Radio>
              <Radio name="3">
                <SvgIcon
                  className="editbox-item-icon editbox-item-icon--24"
                  type="laugh-grey-color"
                />
              </Radio>
              <Radio name="1">
                <SvgIcon
                  className="editbox-item-icon editbox-item-icon--24"
                  type="laugh-red-color"
                />
              </Radio>
            </Radio.Group>
          </div>
          {Array.isArray(subTopicValue) &&
            subTopicValue?.length > 0 &&
            TopicSentimentEditBox({ subTopicValue, updateSubTopicValue })}
          <span className="feeds-editbox-title">
            {formatMessage({ id: 'Editing History' })}
          </span>
          <div className="feeds-editbox-history">
            {logLoading ? (
              <div style={{ textAlign: 'center' }}>
                <Icon type="loading" />
              </div>
            ) : logList.length === 0 ? (
              <div style={{ textAlign: 'center' }}>
                {formatMessage({ id: 'Not yet edited' })}
              </div>
            ) : (
              logList.map(
                (
                  { new_value, editor, create_time, topic }: any,
                  index: number,
                ) => {
                  return (
                    <div key={create_time} className="editbox-item">
                      <span className="editbox-item-infobox">
                        <span className="editbox-item-info">
                          <SvgIcon
                            className="editbox-item-icon"
                            type={getSentimentIcon(new_value)}
                          />
                        </span>
                      </span>
                      <span className="editbox-item-sub-topic">
                        <span>
                          {topic && (transTopic?.(topic, 'L2') || topic)}
                        </span>
                      </span>
                      <span className="editbox-item-name">
                        <BubbleCommon
                          tipsMap={
                            editor === 'NLP_Analyse' ? NLPAnalyseTipsText : null
                          }
                          showIcon={false}
                        >
                          {editor}
                        </BubbleCommon>
                      </span>
                      <span className="editbox-item-text">{create_time}</span>
                    </div>
                  );
                },
              )
            )}
          </div>
        </>
      }
      ft={
        <>
          <Bubble closeOnScroll content={tips} dark>
            <Button
              type="primary"
              disabled={disabled}
              loading={loading}
              onClick={(e: any) => {
                e.stopPropagation();
                const handleAsyncAction = async () => {
                  const amendedSubTopic = subTopicValue?.reduce(
                    (result, newItem) => {
                      const oldItem = subTopic?.find(
                        (item) => item.topic === newItem.topic,
                      );

                      if (!oldItem || oldItem.sentiment !== newItem.sentiment) {
                        result.push({
                          ...newItem,
                          old_sentiment: oldItem.sentiment,
                        });
                      }

                      return result;
                    },
                    [],
                  );
                  if (modifiedValue !== newValue) {
                    await submit();
                    changeNewSentiment(newValue);
                  }
                  if (amendedSubTopic && amendedSubTopic.length > 0) {
                    await submitSubTopic(amendedSubTopic);
                    changeNewSubTopic(subTopicValue);
                  }
                  close();
                };
                handleAsyncAction();
              }}
            >
              {formatMessage({ id: 'Confirm' })}
            </Button>
          </Bubble>
          <Button
            disabled={loading}
            type="weak"
            onClick={(e: any) => {
              e.stopPropagation();
              close();
            }}
          >
            {formatMessage({ id: 'Cancel' })}
          </Button>
        </>
      }
    />
  );
}

function TopicSentimentEditBox({ subTopicValue, updateSubTopicValue }: any) {
  const { formatMessage } = useIntl();
  const [hideTopicList, setHideTopicList] = useState(true);

  return (
    <div>
      <span
        className="feeds-editbox-title"
        onClick={() => setHideTopicList(!hideTopicList)}
      >
        {formatMessage({ id: 'Topic Sentiment' })}
        <SvgIcon type="arrow-down" />
      </span>
      {!!!hideTopicList && (
        <div className="feeds-editbox-radiobox-topic-wrapper">
          {subTopicValue.map((each) => {
            const { topic, sentiment = 0 } = each;

            const radioOptions = [
              ...(sentiment === 0
                ? [{ value: '0', icon: 'face-question' }]
                : []),
              { value: 5, icon: 'laugh-green-color' },
              { value: 3, icon: 'laugh-grey-color' },
              { value: 1, icon: 'laugh-red-color' },
            ];

            return (
              <div className="feeds-editbox-radiobox-topic" key={topic}>
                <span className="feeds-editbox-topic">
                  {formatMessage({ id: topic })}
                </span>
                <Radio.Group
                  value={sentiment}
                  onChange={(e) => updateSubTopicValue(e, topic)}
                >
                  {radioOptions.map(({ value, icon }) => (
                    <Radio name={value} key={value}>
                      <SvgIcon
                        className="editbox-item-icon editbox-item-icon--24"
                        type={icon}
                      />
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function UpdateSentimentInfoBox({
  data,
  realValue,
  needRequest = true,
  type = 'common',
  sentimentLoading = false,
}: {
  data: any;
  realValue?: SentimentType;
  needRequest?: boolean;
  type?: 'common' | 'news';
  sentimentLoading?: boolean;
}) {
  const {
    sentimentRatingValue,
    filter,
    Sentiment_Update,
    sentimentRealValue,
    changeNewSentiment,
    newSentiment,
  } = useSentimentRating({
    data,
    realValue,
    needRequest,
    type,
  });

  const [newSubTopic, changeNewSubTopic] = useState<Array<any>>([]);

  // const subTopic = useMemo(() => {
  //   let subTopic: any = [];
  //   if (data?.topic_chunks) {
  //     try {
  //       subTopic = JSON.parse(data.topic_chunks);
  //     } catch (error) {}
  //   }
  //   return (
  //     subTopic?.map(
  //       ({ topic, sentiment }: { topic: string; sentiment: number }) => ({
  //         topic,
  //         sentiment,
  //       }),
  //     ) || []
  //   );
  // }, [data]);

  // useEffect(() => {
  //   changeNewSubTopic(subTopic);
  // }, [subTopic]);

  const showValue = newSentiment || sentimentRatingValue;
  return (
    <Dropdown
      button={
        <div className="update-sentiment-info-box">
          {sentimentLoading ? (
            <Icon type={'loading'} className="dbo-title-flex-icon" />
          ) : (
            <>
              <SvgIcon
                type={sentimentTypeMap[showValue]}
                className="dbo-title-flex-icon"
              />
              <SvgIcon
                className="update-sentiment-icon update-sentiment-edit"
                type="icon-arrow-down"
              />
            </>
          )}
        </div>
      }
      destroyOnClose
      appearance="pure"
      clickClose={false}
    >
      {(close) => (
        <SentimentEditBox
          close={close}
          showValue={showValue}
          Sentiment_Update={Sentiment_Update}
          defaultValue={sentimentRatingValue}
          filter={{ ...filter, type: 1 }}
          oldValue={sentimentRealValue}
          changeNewSentiment={changeNewSentiment}
          type={type}
          subTopic={newSubTopic}
          changeNewSubTopic={changeNewSubTopic}
        ></SentimentEditBox>
      )}
    </Dropdown>
  );
}
